import React, { useState, useEffect } from 'react';
import authFetch from '../utils/authFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

function AthleteMessages({ userId, onUnreadCountChange }) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (userId) {
      loadMessages();
    }
  }, [userId]);

  const loadMessages = async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/athlete/messages?user_id=${userId}&limit=10`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setUnreadCount(data.unread_count || 0);
        if (onUnreadCountChange) {
          onUnreadCountChange(data.unread_count || 0);
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await authFetch(`${API_BASE_URL}/athlete/messages/${messageId}/read?user_id=${userId}`, {
        method: 'PUT'
      });
      // Update local state
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, is_read: true } : m
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
      if (onUnreadCountChange) {
        onUnreadCountChange(Math.max(0, unreadCount - 1));
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await authFetch(`${API_BASE_URL}/athlete/messages/read-all?user_id=${userId}`, {
        method: 'PUT'
      });
      setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      setUnreadCount(0);
      if (onUnreadCountChange) {
        onUnreadCountChange(0);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `היום ${date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `אתמול ${date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
    }
  };

  // If no coach (not B2B athlete), don't show anything
  if (messages.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-[#1e293b] to-[#0f172a] rounded-2xl border border-white/10 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">💬</span>
          <div className="text-right">
            <p className="text-white font-medium">הודעות מהמאמן</p>
            {unreadCount > 0 && (
              <p className="text-purple-400 text-sm">{unreadCount} הודעות חדשות</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center font-bold">
              {unreadCount}
            </span>
          )}
          <span className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </div>
      </button>

      {/* Messages List */}
      {isExpanded && (
        <div className="border-t border-white/10">
          {isLoading ? (
            <div className="p-4 text-center">
              <span className="animate-spin text-2xl">⏳</span>
            </div>
          ) : messages.length === 0 ? (
            <div className="p-6 text-center">
              <span className="text-3xl mb-2 block">📭</span>
              <p className="text-gray-500">אין הודעות עדיין</p>
            </div>
          ) : (
            <>
              {/* Mark all as read button */}
              {unreadCount > 0 && (
                <div className="px-4 py-2 border-b border-white/5">
                  <button
                    onClick={markAllAsRead}
                    className="text-purple-400 text-sm hover:text-purple-300 transition"
                  >
                    ✓ סמן הכל כנקרא
                  </button>
                </div>
              )}

              <div className="max-h-80 overflow-y-auto">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    onClick={() => !msg.is_read && markAsRead(msg.id)}
                    className={`p-4 border-b border-white/5 transition cursor-pointer ${
                      !msg.is_read 
                        ? 'bg-purple-500/10 hover:bg-purple-500/15' 
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-purple-400 font-medium text-sm">
                            {msg.coach_name || 'המאמן'}
                          </span>
                          {!msg.is_read && (
                            <span className="px-2 py-0.5 rounded-full bg-purple-500 text-white text-xs">
                              חדש
                            </span>
                          )}
                          {msg.type === 'workout_analysis' && (
                            <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-xs">
                              📊 ניתוח
                            </span>
                          )}
                        </div>
                        <p className="text-white text-sm whitespace-pre-wrap leading-relaxed">
                          {msg.text}
                        </p>
                      </div>
                      <span className="text-gray-500 text-xs whitespace-nowrap">
                        {formatDate(msg.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Badge component for nav/header
export function MessagesBadge({ count }) {
  if (!count || count === 0) return null;
  
  return (
    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center font-bold animate-pulse">
      {count > 9 ? '9+' : count}
    </span>
  );
}

export default AthleteMessages;
